/* eslint-disable camelcase */
import { Button, Row, useButtonConfirmDialog, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  DeviceType,
  HeatingZoneID,
  PhysicalLocationID,
  RoomDialWrf06,
  RuleActionType,
  RuleCategory,
  DeviceInfo,
} from '../../../../shared/api-clients/_api';
import { generateSelectOptions } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { BackButton } from '../../components/buttons/back-button';
import { SelectInstallLocation } from '../../components/select-install-location';
import { FilterSelect } from '../../../../shared/components/filter-select';
import { useBeeConfig } from '../../hooks/beeConfig';
import { useRoutePaths } from '../../../../shared/context/paths';
import { routePaths } from '../../routes';
import { DialDescriptor } from './dial-descriptor';
import {
  modbusChannels,
  modbusAddresses,
  modbusChannelDescription,
} from '../../../../shared/helpers/configurationOptionGenerator';
import { getDeviceName } from '../assign-heating-zones/utils';
import { useZones } from '../../hooks/zones';

import './configure-dial.scss';
import { Spinner } from '../../components/spinner/spinner';

type IConfigureDialForm = {
  modbusChannel: string;
  modbusAddress: number;
  location: PhysicalLocationID;
};

export const validators: InputValidator<IConfigureDialForm>[] = [
  {
    key: 'location',
    validators: [
      {
        validate: value =>
          Object.values(HeatingZoneID)
            .filter(hz => hz !== HeatingZoneID.WHOLE_HOME.toString())
            .includes(value),
        message: 'Please select a location',
      },
    ],
  },
  {
    key: 'modbusChannel',
    validators: [
      {
        validate: value => modbusChannels.includes(value),
        message: 'Please select a Modbus Channel',
      },
    ],
  },
  {
    key: 'modbusAddress',
    validators: [
      {
        validate: value => modbusAddresses.includes(+value),
        message: 'Please select a Modbus Address',
      },
    ],
  },
];

const initialState: IConfigureDialForm = {
  location: null,
  modbusAddress: null,
  modbusChannel: null,
};

export const ConfigureDialView: React.FunctionComponent = () => {
  const { devices, createDevice, editDevice, removeDevice, getDevicesByType, getAssignedResourcesFor, loading } =
    useBeeConfig();
  const { installLocation: currentInstallLocation } = useParams<{ installLocation: PhysicalLocationID }>();
  const { replace } = useRoutePaths();
  const { getZoneDescription } = useZones();

  const { initState, currentDialControl, currentDialMonitoring } = React.useMemo(() => {
    const tempDialControl = getDevicesByType<RoomDialWrf06>([DeviceType.WRF06]).find(
      ({ params: { install_location, action_type } }) =>
        install_location === currentInstallLocation && action_type === RuleActionType.CONTROL
    );
    const tempDialMonitoring = getDevicesByType<RoomDialWrf06>([DeviceType.WRF06]).find(
      ({ params: { install_location, action_type } }) =>
        install_location === currentInstallLocation && action_type === RuleActionType.MONITORING
    );
    return {
      initState: tempDialControl
        ? {
            location: currentInstallLocation,
            modbusAddress: tempDialControl.params.address,
            modbusChannel: tempDialControl.params?.channel,
          }
        : initialState,
      currentDialControl: tempDialControl,
      currentDialMonitoring: tempDialMonitoring,
    };
  }, [devices]);

  const { DataForm, bind, dataBinder } = useForm(initState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IConfigureDialForm>(validators, dataBinder);

  const handleSave = () => {
    const install_location = dataBinder.getValue('location');
    const channel = dataBinder.getValue('modbusChannel');
    const address = +dataBinder.getValue('modbusAddress');

    const wrf06DevicesToAdd: DeviceInfo[] = [
      {
        deviceType: DeviceType.WRF06,
        params: {
          address,
          install_location,
          channel,
          zone_id: null,
          category: RuleCategory.TEMPERATURE,
          action_type: RuleActionType.CONTROL,
        },
      },
      {
        deviceType: DeviceType.WRF06,
        params: {
          address,
          install_location,
          channel,
          zone_id: null,
          category: RuleCategory.TEMPERATURE,
          action_type: RuleActionType.MONITORING,
          primary_zone_temperature_sensor: 0,
        },
      },
    ];

    createDevice(...wrf06DevicesToAdd);

    replace(routePaths.thermostat.addDial);
  };

  const handleEdit = React.useCallback(() => {
    const install_location = dataBinder.getValue('location');
    const channel = dataBinder.getValue('modbusChannel');
    const address = +dataBinder.getValue('modbusAddress');

    editDevice(currentDialControl, {
      ...currentDialControl,
      params: { ...currentDialControl.params, address, install_location, channel },
    });
    editDevice(currentDialMonitoring, {
      ...currentDialMonitoring,
      params: { ...currentDialMonitoring.params, address, install_location, channel },
    });

    replace(routePaths.thermostat.addDial);
  }, [currentInstallLocation, currentDialControl, currentDialMonitoring]);

  const handleDelete = React.useCallback(() => {
    removeDevice(currentDialControl);
    removeDevice(currentDialMonitoring);
    replace(routePaths.thermostat.addDial);
  }, [currentInstallLocation, currentDialControl, currentDialMonitoring]);

  const deletable = useButtonConfirmDialog({
    content: currentDialControl && `Are you sure you want to delete this ${getDeviceName(currentDialControl)}?`,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  const noDelete = useButtonConfirmDialog({
    content:
      currentDialControl &&
      `You can't delete this ${getDeviceName(currentDialControl)}, as it's assigned to the ${getZoneDescription(
        currentDialControl.params.zone_id
      )} Heating Zone`,
    confirmText: 'OK',
    cancelText: 'Cancel',
  });

  const modbusAddressOptions = generateSelectOptions(modbusAddresses, true);
  const modbusChannelOptions = generateSelectOptions(modbusChannels).map(c => ({
    ...c,
    name: modbusChannelDescription[c.id],
  }));

  const assignedLocations = getAssignedResourcesFor(
    [DeviceType.WRF06],
    'install_location',
    dataBinder.getValue('location')
  );

  const assignedAddresses = getAssignedResourcesFor(
    [DeviceType.WRF06],
    'address',
    dataBinder.getValue('modbusAddress')
  ).map(value => +value);

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Configuration <span className="color-primary">Dial</span>
      </h1>
      <DialDescriptor />
      <div className="row">
        <SelectInstallLocation
          {...bind.select('location')}
          label="Where is the device installed?"
          onBlur={setTouched}
          filterOptions={assignedLocations}
        />
      </div>
      <div className="row">
        <FilterSelect
          {...bind.select('modbusChannel')}
          label="Enter the MODBUS channel:"
          options={modbusChannelOptions}
          onBlur={setTouched}
          data-qa="modbus-channel"
        />
      </div>
      <div className="row">
        <FilterSelect
          {...bind.select('modbusAddress')}
          label="Enter the MODBUS address (Living room = 11, First floor = 12):"
          options={modbusAddressOptions}
          filterOptions={assignedAddresses}
          onBlur={setTouched}
          data-qa="modbus-address"
        />
      </div>
      <Row className="btn-row">
        {currentDialControl && (
          <Button
            className="btn-full secondary-button"
            onClick={async () => {
              if (!currentDialControl.params.zone_id && (await deletable())) {
                handleDelete();
              } else {
                noDelete();
              }
            }}
            data-qa="delete-button"
          >
            Delete device
          </Button>
        )}
        <Button
          className="continue-button btn-full btn-large"
          onClick={currentInstallLocation ? handleEdit : handleSave}
          disabled={!formIsValid}
          data-qa="continue-button"
        >
          {currentDialControl ? 'Save Changes' : 'Save'}
        </Button>
      </Row>
    </DataForm>
  );
};
